import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ProvisionalSchoolsService } from 'src/app/component/children/service/provisional-schools.service';
import { ConfirmPopoverComponent } from 'src/app/component/common-components/confirm-popover/confirm-popover.component';
import { MsgModel } from 'src/app/component/common-components/msg/model/msg.model';

@Component({
    selector: 'app-edit-provisional-school-information',
    templateUrl: './edit-provisional-school-information.component.html',
    styleUrl: './edit-provisional-school-information.component.scss'
})
export class EditProvisionalSchoolInformationComponent implements OnInit {
    @Input() provisionalSchool: any;
    @Output() provisionalSchoolChange = new EventEmitter<any>();

    @ViewChild('savePopover') savePopover!: ConfirmPopoverComponent;

    form!: FormGroup;

    // UI state
    submitted = false;
    saving = false;
    saved = false;
    showMessage = false;
    msg: MsgModel;

    // backup for cancel
    private originalData: any;

    constructor(
        private fb: FormBuilder,
        private provisionalSchoolsService: ProvisionalSchoolsService
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.loadData();
    }

    /****************************************************************************************
     * INIT
     ****************************************************************************************/

    private initForm(): void {
        this.form = this.fb.group({
            SCHOOL_NAME: ['', Validators.required],
            DISTRICT: ['', Validators.required],
            STREET_ADDRESS_1: ['', Validators.required],
            STREET_ADDRESS_2: [''],
            CITY: ['', Validators.required],
            ZIPCODE: ['', Validators.required]
        });
    }

    private loadData(): void {
        if (!this.provisionalSchool) return;

        // deep copy for cancel
        this.originalData = structuredClone(this.provisionalSchool);

        // populate form
        this.form.patchValue(this.provisionalSchool);
    }

    /****************************************************************************************
     * ACTIONS
     ****************************************************************************************/

    triggerConfirmPopover(event: Event) {
        const keyboardEvent = event as KeyboardEvent;
        keyboardEvent.preventDefault(); // prevent default form submit
        if (!this.saving && this.form.valid) {
            this.savePopover.openPopover(); // open the confirm popover programmatically
        }
    }

    onSubmit(): void {
        this.submitted = true;

        if (this.form.invalid) {
            this.handleValidationError();
            return;
        }

        this.saving = true;
        this.showMessage = false;

        const payload = {
            ...this.originalData,
            ...this.form.value
        };

        this.provisionalSchoolsService.update(payload).subscribe({
            next: (res: any) => this.handleResponse(res),
            error: () => this.handleError()
        });
    }

    cancel(): void {
        // restore original values into form
        this.form.reset(this.originalData);

        this.submitted = false;
        this.showMessage = false;

        this.provisionalSchoolChange.emit(this.originalData);
    }

    /****************************************************************************************
     * HELPERS
     ****************************************************************************************/

    getValidationClass(controlName: string) {
        const control = this.form.get(controlName);

        return {
            'has-success': control?.valid && this.submitted,
            'has-error': control?.invalid && this.submitted
        };
    }

    private handleResponse(res: any): void {
        if (res?.msg?.SUCCESS === true) {
            this.saved = true;
            this.msg = res.msg;
            this.showMessage = true;

            this.provisionalSchoolChange.emit(res.editProvisionalSchool);
        } else if (res?.msg?.SUCCESS === false) {
            this.saved = false;
            this.msg = res.msg;
            this.showMessage = true;
            this.saving = false;
        } else {
            this.handleGenericFailure();
        }
    }

    private handleError(): void {
        this.handleGenericFailure();
    }

    private handleGenericFailure(): void {
        this.saved = false;
        this.msg = new MsgModel();
        this.msg.MSG = 'Something went wrong, please try again.';
        this.msg.SUCCESS = false;
        this.showMessage = true;
        this.saving = false;
    }

    private handleValidationError(): void {
        this.showMessage = true;
        this.msg = new MsgModel();
        this.msg.MSG = 'There are errors, please check your input(s).';
        this.msg.SUCCESS = false;
    }
}
